import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';

type PillBadgeTone = 'default' | 'accent' | 'solid';

const PILL_BADGE_CLASS =
  'inline-flex shrink-0 items-center gap-1.5 font-medium whitespace-nowrap tabular-nums';

const PILL_BADGE_TONE_CLASS: Record<PillBadgeTone, string> = {
  default: cn(
    'rounded-full bg-card/80 py-0.5 pr-2.5 pl-0.5 text-xs text-foreground/80 ring-1 ring-primary/20',
    'shadow-[0_4px_12px_-8px_color-mix(in_oklab,var(--color-primary)_60%,transparent)]',
  ),
  accent:
    'h-7 rounded-lg bg-card pr-3 pl-0.75 text-xs-plus text-foreground ring-1 ring-brand-link/28',
  solid: cn('rounded-full py-0.5 pr-2.5 pl-0.5 text-xs', ACCENT_MARK_CLASS),
};

const PILL_BADGE_ICON_CLASS: Record<PillBadgeTone, string> = {
  default: 'flex size-5.5 items-center justify-center rounded-full bg-primary/10 text-primary',
  accent: 'flex size-5.5 items-center justify-center rounded-md bg-brand-link text-white',
  solid:
    'flex size-5.5 items-center justify-center rounded-full bg-primary-foreground/15 text-primary-foreground',
};

const PILL_BADGE_ICON_SIZE_CLASS: Record<PillBadgeTone, string> = {
  default: 'size-3',
  accent: 'size-3.25',
  solid: 'size-3',
};

interface PillBadgeProps {
  icon: LucideIcon;
  screenReaderLabel: string;
  children: ReactNode;
  tone?: PillBadgeTone | undefined;
}

export function PillBadge({
  icon: Icon,
  screenReaderLabel,
  children,
  tone = 'default',
}: PillBadgeProps) {
  return (
    <span className={cn(PILL_BADGE_CLASS, PILL_BADGE_TONE_CLASS[tone])}>
      <span className={PILL_BADGE_ICON_CLASS[tone]}>
        <Icon className={PILL_BADGE_ICON_SIZE_CLASS[tone]} aria-hidden />
      </span>
      <span className="sr-only">{screenReaderLabel}</span>
      {children}
    </span>
  );
}
